import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { payApi, type PayPeriod } from "../../../api/pay";
import { reportsApi, type Report, type ReportFilters } from "../../../api/reports";
import { adminApi, venueApi } from "../../../api/resources";
import { PageHeader } from "../../../components/ComingSoon";
import { Modal } from "../../../components/Overlay";
import { Button } from "../../../components/ui";
import { useLoad } from "../shared";

const KEYS: [string, string][] = [["timesheets", "Timesheets"], ["venue-hours", "Venue hours"], ["overtime", "Regular & OT"], ["gross", "Gross wages"],
  ["payroll-status", "Approved vs pending"], ["payment-register", "Payment register"], ["exceptions", "Unreconciled & exceptions"], ["audit", "Correction & audit"]];
const ENTITY = ["", "correction_request", "session_revision", "pay_period", "payroll_run", "payment_record", "payment_obligation", "overpayment", "payment_election", "launch_signoff"];
const sel = "h-10 rounded-sm border border-white/15 bg-surface px-3 text-sm text-white";
const cell = (v: unknown) => (v === null || v === undefined ? "" : String(v));

function Filters({ k, f, set }: { k: string; f: ReportFilters; set: (f: ReportFilters) => void }) {
  const { data: periods } = useLoad(useCallback(() => payApi.periods(), []));
  const { data: venues } = useLoad(useCallback(() => venueApi.list(), []));
  const { data: emps } = useLoad(useCallback(() => adminApi.employees(), []));
  const ch = (key: keyof ReportFilters) => (e: { target: { value: string } }) => set({ ...f, [key]: e.target.value });
  return (
    <div className="flex flex-wrap gap-2" data-testid="report-filters">
      <select className={sel} value={f.period_id ?? ""} onChange={ch("period_id")} data-testid="report-filter-period">
        <option value="">{k === "payroll-status" || k === "payment-register" || k === "exceptions" ? "All periods" : "Current period"}</option>
        {(periods ?? []).map((p: PayPeriod) => <option key={p.id} value={p.id}>{`${p.start_date} – ${p.end_date} · ${p.status}`}</option>)}
      </select>
      <input type="date" className={sel} value={f.date_from ?? ""} onChange={ch("date_from")} data-testid="report-filter-from" />
      <input type="date" className={sel} value={f.date_to ?? ""} onChange={ch("date_to")} data-testid="report-filter-to" />
      {["timesheets", "venue-hours", "overtime"].includes(k) && (
        <select className={sel} value={f.venue_id ?? ""} onChange={ch("venue_id")} data-testid="report-filter-venue">
          <option value="">All venues</option>{(venues ?? []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>)}
      {k !== "audit" && (
        <select className={sel} value={f.employee_id ?? ""} onChange={ch("employee_id")} data-testid="report-filter-employee">
          <option value="">All employees</option>{(emps ?? []).map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
        </select>)}
      {k === "payment-register" && (
        <select className={sel} value={f.method ?? "ZELLE"} onChange={ch("method")} data-testid="report-filter-method">
          {["ZELLE", "CHECK", "CASH", "ALL"].map((m) => <option key={m}>{m}</option>)}
        </select>)}
      {k === "audit" && (
        <select className={sel} value={f.entity_type ?? ""} onChange={ch("entity_type")} data-testid="report-filter-entity">
          {ENTITY.map((x) => <option key={x} value={x}>{x || "All entity types"}</option>)}
        </select>)}
    </div>
  );
}

function Table({ r }: { r: Report }) {
  return (
    <div className="overflow-x-auto rounded-sm border border-white/10">
      <table className="w-full text-left text-xs text-zinc-300" data-testid="report-table">
        <thead><tr className="label-caps text-zinc-500">{r.columns.map((c) => <th key={c} className="px-2 py-2 font-normal">{c}</th>)}</tr></thead>
        <tbody>
          {r.rows.map((row, i) => (
            <tr key={i} className={`border-t border-white/5 ${row[row.length - 1] === "SUBTOTAL" ? "bg-white/5 font-semibold text-white" : ""}`} data-testid={`report-row-${i}`}>
              {row.map((v, j) => <td key={j} className="whitespace-nowrap px-2 py-1.5 font-mono">{cell(v)}</td>)}
            </tr>
          ))}
          {!r.rows.length && <tr><td className="px-2 py-6 text-zinc-500" colSpan={r.columns.length} data-testid="report-empty">No rows for these filters.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminReports() {
  const [k, setK] = useState("timesheets");
  const [f, setF] = useState<ReportFilters>({});
  const [full, setFull] = useState(false);
  const [ask, setAsk] = useState(false);
  const [r, setR] = useState<Report | null>(null);
  useEffect(() => {
    setR(null);
    reportsApi.run(k, f, full && k === "payment-register").then(setR).catch((e) => toast.error(errorMessage(e)));
  }, [k, f, full]);
  const pick = (key: string) => { setK(key); setF({}); setFull(false); };
  return (
    <div data-testid="admin-reports-page" className="animate-rise space-y-6">
      <PageHeader title="Reports" />
      <div className="flex flex-wrap gap-2" data-testid="report-tabs">
        {KEYS.map(([key, l]) => (
          <button key={key} onClick={() => pick(key)} data-testid={`report-tab-${key}`}
            className={`rounded-sm border px-3 py-1.5 text-xs font-semibold transition-colors ${k === key ? "border-signal bg-signal/10 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <Filters k={k} f={f} set={setF} />
      {r && (
        <>
          <p className="font-display text-2xl font-bold text-white" data-testid="report-showing">{r.showing}</p>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className={`rounded-sm px-2 py-1 font-semibold ${r.label.startsWith("FINAL") ? "bg-emerald-500/15 text-emerald-200" : "bg-amber-500/15 text-amber-200"}`} data-testid="report-label">{r.label}</span>
            <span className="text-zinc-400" data-testid="report-policy-status">Policy {r.policy_status}</span>
            <span className="text-zinc-500" data-testid="report-gross-label">{r.gross_label}</span>
            {k === "payment-register" && (
              <label className="flex items-center gap-2 text-zinc-300"><input type="checkbox" checked={full} data-testid="report-full-recipients"
                onChange={(e) => (e.target.checked ? setAsk(true) : setFull(false))} />Include full recipient identifiers</label>)}
            <a href={reportsApi.csv(k, f, full && k === "payment-register")} className="ml-auto font-semibold text-signal hover:text-white" data-testid="report-export-csv">Export CSV</a>
          </div>
          <Table r={r} />
          <div className="flex flex-wrap gap-3 text-xs" data-testid="report-totals">
            {Object.entries(r.totals).map(([t, v]) => <span key={t} className="rounded-sm border border-white/15 px-2 py-1 font-mono text-white" data-testid={`report-total-${t}`}>{t}: {cell(v)}</span>)}
          </div>
          {r.notes.map((n) => <p key={n} className="text-xs text-zinc-500">{n}</p>)}
        </>
      )}
      <Modal open={ask} title="Include full recipient identifiers?" onClose={() => setAsk(false)} testId="full-recipients-dialog">
        <p className="mb-4 text-sm text-zinc-300">Full Zelle phone numbers/emails will be shown and exported. This is recorded in the audit log.</p>
        <Button onClick={() => { setFull(true); setAsk(false); }} data-testid="full-recipients-confirm">Include and audit</Button>
      </Modal>
    </div>
  );
}
